import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { subscriptionService, SubscriptionTier, PLAN_LABELS, PLAN_PRICES } from '../../services/subscriptionService'
import styles from './TrialExpirationModal.module.css'

interface TierFeatures {
  tier: SubscriptionTier
  name: string
  price: number
  description: string
  features: Array<{ label: string; included: boolean }>
  recommended?: boolean
}

interface ValidationWarning {
  type: string
  message: string
  feature?: string
  action_required: string
}

interface TrialExpirationModalProps {
  onConverted?: () => void
}

const tierFeatures: TierFeatures[] = [
  {
    tier: 'starter',
    name: PLAN_LABELS.starter,
    price: PLAN_PRICES.starter,
    description: 'Perfect for small teams getting started',
    features: [
      { label: 'Up to 5 paid seats (admin, manager)', included: true },
      { label: 'Up to 25 total users', included: true },
      { label: 'Up to 10 projects', included: true },
      { label: 'Basic resource management', included: true },
      { label: 'Partner rate sheets', included: false },
      { label: 'Revenue forecasting', included: false },
      { label: 'Gantt charts', included: false },
      { label: 'Audit logs', included: false },
      { label: 'SSO authentication', included: false },
    ],
  },
  {
    tier: 'professional',
    name: PLAN_LABELS.professional,
    price: PLAN_PRICES.professional,
    description: 'For growing teams that need more',
    recommended: true,
    features: [
      { label: 'Up to 15 paid seats (admin, manager)', included: true },
      { label: 'Up to 100 total users', included: true },
      { label: 'Up to 50 projects', included: true },
      { label: 'Advanced resource management', included: true },
      { label: 'Partner rate sheets', included: true },
      { label: 'Revenue forecasting', included: true },
      { label: 'Gantt charts', included: true },
      { label: 'Audit logs', included: false },
      { label: 'SSO authentication', included: false },
    ],
  },
  {
    tier: 'enterprise',
    name: PLAN_LABELS.enterprise,
    price: PLAN_PRICES.enterprise,
    description: 'Full platform access for large teams',
    features: [
      { label: 'Unlimited paid seats', included: true },
      { label: 'Unlimited users', included: true },
      { label: 'Unlimited projects', included: true },
      { label: 'Advanced resource management', included: true },
      { label: 'Partner rate sheets', included: true },
      { label: 'Revenue forecasting', included: true },
      { label: 'Gantt charts', included: true },
      { label: 'Audit logs', included: true },
      { label: 'SSO authentication', included: true },
    ],
  },
]

export function TrialExpirationModal({ onConverted }: TrialExpirationModalProps) {
  const { organization, signOut } = useAuth()
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Processing...')
  const [error, setError] = useState('')
  const [warnings, setWarnings] = useState<ValidationWarning[]>([])
  const [featureAcknowledged, setFeatureAcknowledged] = useState(false)
  const [understandDowngrade, setUnderstandDowngrade] = useState(false)

  useEffect(() => {
    if (selectedTier && selectedTier !== 'enterprise') {
      validateTierSelection(selectedTier)
    } else {
      setWarnings([])
    }
  }, [selectedTier])

  const validateTierSelection = async (tier: SubscriptionTier) => {
    if (!organization) return
    try {
      const data = await subscriptionService.getChangePreview(organization.id, tier)
      const featureLossWarnings = (data?.warnings || []).filter(
        (w: ValidationWarning) => w.type === 'feature_loss'
      )
      setWarnings(featureLossWarnings)
    } catch {
      // validation is best-effort
    }
  }

  const handleSelectPlan = async () => {
    if (!selectedTier || !organization) return

    if (selectedTier !== 'enterprise' && warnings.length > 0 && !featureAcknowledged) {
      setError('Please acknowledge that you understand the feature limitations')
      return
    }

    if (selectedTier !== 'enterprise' && !understandDowngrade) {
      setError('Please confirm you understand you are selecting a lower tier than your trial')
      return
    }

    setLoading(true)
    setError('')
    setLoadingMessage('Preparing your plan...')

    try {
      const result = await subscriptionService.upgradePlan(organization.id, selectedTier)

      if (result?.checkoutUrl) {
        setLoadingMessage('Redirecting to payment...')
        window.location.href = result.checkoutUrl
      } else {
        onConverted?.()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to activate plan. Please try again.')
      setLoading(false)
    }
  }

  const isDowngrade = selectedTier && selectedTier !== 'enterprise'

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Your Trial Has Ended</h2>
          <p className={styles.subtitle}>
            Choose a plan to continue using AllocatIQ
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.trialBanner}>
            <h3>You've experienced the full Enterprise platform</h3>
            <p>
              During your trial, you had access to all Enterprise features.
              Select the plan that best fits your team's needs to continue.
            </p>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.planGrid}>
            {tierFeatures.map((plan) => (
              <div
                key={plan.tier}
                className={`${styles.planCard} ${
                  selectedTier === plan.tier ? styles.selected : ''
                } ${plan.recommended ? styles.recommended : ''}`}
                onClick={() => {
                  setSelectedTier(plan.tier)
                  setError('')
                  setUnderstandDowngrade(false)
                  setFeatureAcknowledged(false)
                }}
              >
                {plan.recommended && (
                  <div className={styles.recommendedBadge}>Recommended</div>
                )}
                <h3 className={styles.planName}>{plan.name}</h3>
                <div className={styles.planPrice}>
                  ${plan.price}
                  <span>/month</span>
                </div>
                <p className={styles.planDescription}>{plan.description}</p>
                <ul className={styles.featureList}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={feature.included ? '' : styles.missing}>
                      {feature.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {isDowngrade && warnings.length > 0 && (
            <div className={styles.warningSection}>
              <div className={styles.warningTitle}>Features You Will Lose</div>
              <ul className={styles.warningList}>
                {warnings.map((warning, idx) => (
                  <li key={idx}>{warning.message}</li>
                ))}
              </ul>
            </div>
          )}

          {isDowngrade && (
            <div className={styles.confirmationSection}>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="understand-downgrade"
                  checked={understandDowngrade}
                  onChange={(e) => setUnderstandDowngrade(e.target.checked)}
                />
                <label htmlFor="understand-downgrade">
                  I understand I am selecting the {PLAN_LABELS[selectedTier!]} plan,
                  which has fewer features than the Enterprise plan I used during my trial
                </label>
              </div>
              {warnings.length > 0 && (
                <div className={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="acknowledge-features"
                    checked={featureAcknowledged}
                    onChange={(e) => setFeatureAcknowledged(e.target.checked)}
                  />
                  <label htmlFor="acknowledge-features">
                    I acknowledge that I will lose access to the features listed above
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>{loadingMessage}</p>
            </div>
          ) : (
            <>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleSelectPlan}
                disabled={!selectedTier}
              >
                Continue with {selectedTier ? PLAN_LABELS[selectedTier] : 'Selected'} Plan
              </button>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={signOut}
              >
                Sign out
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
